/**
 * La scène des éditeurs en plan de travail (image et vidéo) : le canvas fabric remplit le
 * panneau, le cadre du document y est centré par la vue. Les couches HTML qui doivent
 * tomber exactement sous le cadre — damier de transparence, fonds HTML transitoires,
 * iframe vidéo — s'y calent ici, et le suivent à chaque ajustement, zoom ou déplacement.
 *
 * Usage :
 *   import { followFrame, frameClientRect } from "../lib/editor_stage";
 *   followFrame(editor, frameLayerEl);   // frameLayerEl : frère du container fabric
 *
 * Plan : docs/explanation/editor-workspace-viewport.md.
 */
import type { FabricEditor, WorkspaceOptions } from "@frabr/fabric-editor";

/**
 * Le plan de travail des deux éditeurs : un gris uni (le `bg-editor_stage` du panneau, sous le
 * canvas), le hors-cadre voilé, le cadre à 32 px des bords (12 sur mobile). Le damier est réservé à une page transparente — dans le
 * cadre, sur la couche `frame`. La couleur est lue sur `el`, qui porte le mode (clair/sombre)
 * et la marque : le canvas ne connaît pas les variables CSS.
 */
export function workspace(el: Element): WorkspaceOptions {
  return { veil: "solid", color: themeColor(el, "editor_stage") ?? "#f3f4f6", margin: workspaceMargin() };
}

/** Le mobile de Tailwind (sous `md`) : la place y est comptée, le cadre s'y écarte moins des bords. */
const MOBILE = "(max-width: 767px)";
const MARGIN = 32;
const MOBILE_MARGIN = 12;

/** La marge du cadre dans le plan de travail, lue au montage de l'éditeur. */
function workspaceMargin(): number {
  return typeof window !== "undefined" && window.matchMedia?.(MOBILE).matches ? MOBILE_MARGIN : MARGIN;
}

/**
 * Une couleur de la charte, lue sur `el` (qui porte la marque et le mode) pour le canvas :
 * les tokens Tailwind sont des triplets `R G B` dans `--color-<nom>`. Null si absent.
 */
export function themeColor(el: Element, name: string): string | null {
  const rgb = getComputedStyle(el).getPropertyValue(`--color-${name}`).trim().split(/\s+/);
  return rgb.length === 3 ? `rgb(${rgb.join(",")})` : null;
}

/**
 * Cale `el` sur le cadre (position absolue, mêmes px que l'élément canvas : `el` doit
 * partager l'origine du container fabric, typiquement un frère en `absolute`).
 */
export function followFrame(editor: FabricEditor, el: HTMLElement): void {
  const place = () => {
    const frame = editor.frameRect;
    el.style.left = `${frame.left}px`;
    el.style.top = `${frame.top}px`;
    el.style.width = `${frame.width}px`;
    el.style.height = `${frame.height}px`;
  };
  editor.onResize(place);
  place();
}

/** Le cadre en coordonnées de la fenêtre (pour comparer à un `clientX/Y`). */
export function frameClientRect(editor: FabricEditor, container: HTMLElement): DOMRect {
  const canvasEl = container.querySelector<HTMLElement>(".canvas-container") ?? container;
  const origin = canvasEl.getBoundingClientRect();
  const frame = editor.frameRect;
  return new DOMRect(origin.left + frame.left, origin.top + frame.top, frame.width, frame.height);
}

/**
 * Le POINTEUR est dans la bande du fond : ~50 px le long des bords du cadre (un quart du
 * cadre au plus), et 36 px au-delà — on tient une image par n'importe où, le geste dépasse
 * facilement le cadre. `whole` : tout le cadre compte (une page ou une séquence vide).
 */
export function nearFrameEdge(
  editor: FabricEditor, container: HTMLElement, point: { clientX: number; clientY: number }, { whole = false } = {},
): boolean {
  const box = frameClientRect(editor, container);
  const { clientX: x, clientY: y } = point;
  const outside = 36;
  if (x < box.left - outside || x > box.right + outside || y < box.top - outside || y > box.bottom + outside) return false;
  if (whole) return true;

  const band = Math.min(50, box.width / 4, box.height / 4);
  return x - box.left < band || box.right - x < band || y - box.top < band || box.bottom - y < band;
}

/**
 * Le nom d'une page (ou d'une séquence) affiché au-dessus du cadre : le gabarit du serveur
 * (« Slide %{number} »), ou le libellé qu'elle porte, puis son format (« Slide 2 · 4:5 »).
 */
export function pageName(template: string, number: number, width: number, height: number, label?: string): string {
  return `${label || template.replace("%{number}", String(number))} · ${ratioLabel(width, height)}`;
}

/** Un format en proportion lisible (1080 × 1350 → « 4:5 »). */
export function ratioLabel(width: number, height: number): string {
  const gcd = (a: number, b: number): number => (b ? gcd(b, a % b) : a);
  const divisor = gcd(Math.round(width), Math.round(height)) || 1;
  return `${Math.round(width) / divisor}:${Math.round(height) / divisor}`;
}
